// src/utils/api.js
import axios from "axios";

/* ===========================================================
 🌐 BASE URL
  - يمنع تكرار /api/api
  - يضمن إن النهاية تكون /api مرة واحدة
=========================================================== */
const normalizeBase = (url) => {
  if (!url) return url;

  let u = String(url).trim().replace(/\/$/, "");

  // يمنع /api/api
  u = u.replace(/\/api\/api$/, "/api");

  // يضمن النهاية /api مرة واحدة فقط
  u = u.replace(/\/api\/?$/, "/api");

  return u;
};

const getBaseURL = () => {
  const envUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "");
  if (envUrl) return normalizeBase(envUrl);

  // Development
  if (window.location.hostname === "localhost") {
    return "http://127.0.0.1:5000/api";
  }

  // Production
  return normalizeBase(`${window.location.origin}/api`);
};

const baseURL = getBaseURL();

/* ===========================================================
 ⚙️ Axios Instance
=========================================================== */
const api = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

/* ===========================================================
 🔧 Helpers (Redirect / Storage)
=========================================================== */
const setAuthBlock = (payload) => {
  try {
    localStorage.setItem(
      "auth_block",
      JSON.stringify({
        code: payload?.code || "",
        message: payload?.message || "",
        subscriptionStatus: payload?.subscriptionStatus,
        subscriptionExpiresAt: payload?.subscriptionExpiresAt,
        ts: Date.now(),
      })
    );
  } catch (_) {}
};

const clearAuth = () => {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    // userId سيبه غالبًا عشان AI/جلسات… لو تحب نمسحه في حالات معينة
    // localStorage.removeItem("userId");
  } catch (_) {}
};

const safeRedirect = (to) => {
  try {
    if (window.location.pathname !== to) window.location.href = to;
  } catch (_) {}
};

/* ===========================================================
 🔐 Request Interceptor — JWT + منع مضاعفة /api/
=========================================================== */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // يمنع إن حد يبعت "/api/..." فتتقلب "/api/api/..."
    if (typeof config.url === "string") {
      config.url = config.url.replace(/^\/api\//, "/");
    }

    return config;
  },
  (err) => Promise.reject(err)
);

/* ===========================================================
 ⚙️ Response Interceptor
  - Logs نظيفة
  - ✅ 403 (Expired/Frozen) → Redirect to /subscribe
  - ✅ 401 (Token issues) → Optional redirect to /login
=========================================================== */
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    const code = data?.code;

    const path = window.location.pathname;

    // ✅ 403: Subscription expired / Account frozen
    if (status === 403 && (code === "SUBSCRIPTION_EXPIRED" || code === "ACCOUNT_FROZEN")) {
      setAuthBlock({
        code,
        message: data?.message,
        subscriptionStatus: data?.subscriptionStatus,
        subscriptionExpiresAt: data?.subscriptionExpiresAt,
      });

      // ما تعملش redirect لو هو أصلاً في صفحات public حساسة
      const allowNoRedirectPaths = ["/login", "/register", "/subscribe", "/payment/success"];
      if (!allowNoRedirectPaths.includes(path)) {
        safeRedirect("/subscribe");
      }
    }

    // ✅ 401: Token expired/invalid → رجّعه ل login (اختياري)
    if (status === 401 && (code === "TOKEN_EXPIRED" || code === "TOKEN_INVALID")) {
      setAuthBlock({ code, message: data?.message });
      clearAuth();

      const allowNoRedirectPaths = ["/login", "/register"];
      if (!allowNoRedirectPaths.includes(path)) {
        safeRedirect("/login");
      }
    }

    // Logs
    if (err?.response) {
      console.error("API Error:", {
        url: err?.config?.url,
        method: err?.config?.method,
        status,
        code,
        data,
      });
    } else {
      console.error("Network/Unknown Error:", err?.message || err);
    }

    return Promise.reject(err);
  }
);

export default api;

/* ===========================================================
 🛠️ Admin: Message Settings
=========================================================== */
export const saveMessageSettings = async ({ maxMessages, pricePerMessage }) => {
  try {
    const res = await api.put("/settings/message-settings", {
      maxMessages,
      pricePerMessage,
    });

    if (res.data?.status === "success") {
      alert("✅ تم حفظ إعدادات الرسائل بنجاح!");
      return true;
    }

    alert("❌ حدث خطأ أثناء حفظ إعدادات الرسائل.");
    return false;
  } catch (err) {
    console.error("❌ saveMessageSettings:", err);
    alert("❌ حدث خطأ أثناء حفظ إعدادات الرسائل.");
    return false;
  }
};

/* ===========================================================
 🤖 sendToAI
  - ✅ Success: يرجّع Object فيه reply + meta
  - ✅ Failure: يرمي axios error نفسه (AIChatPage يقرأ err.response.data)
=========================================================== */
export const sendToAI = async ({
  message,
  file,
  sessionId,
  tool,
  selectedPlan,
  userId: passedUserId,
}) => {
  try {
    // userId: لو الصفحة بعتته نستخدمه، وإلا ناخده من localStorage
    let userId = passedUserId || localStorage.getItem("userId");
    if (!userId) {
      userId = `anon_${Date.now()}`;
      localStorage.setItem("userId", userId);
    }

    const safeMsg = String(message ?? "").trim();
    const safeSessionId = String(sessionId ?? "").trim();
    const safeTool = String(tool ?? "").trim();
    const safePlan = String(selectedPlan ?? "العادي").trim();

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("message", safeMsg);
    formData.append("sessionId", safeSessionId);
    formData.append("tool", safeTool);
    formData.append("selectedPlan", safePlan);
    if (file) formData.append("file", file);

    const res = await api.post("/ai/chat", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });

    const data = res?.data || {};
    const reply = String(data.reply || data.message || "").trim();

    return {
      reply: reply || "⚠️ لم يتم استلام رد من المستشار القانوني.",
      sessionId: data.sessionId || safeSessionId,
      plan: data.plan || safePlan,
      provider: data.provider,
      model: data.model,
      retrieval: data.retrieval,
      raw: data,
    };
  } catch (err) {
    // Timeout
    if (err?.code === "ECONNABORTED") {
      const e = new Error("⏳ انتهت مهلة الاتصال. حاول مجددًا.");
      e.meta = { code: "TIMEOUT" };
      throw e;
    }

    // Server error: سيبها axios error زي ما هي
    if (err?.response) {
      const status = err.response.status;
      const data = err.response.data || {};

      err.message =
        data?.message ||
        data?.error ||
        `❌ حدث خطأ أثناء التواصل مع السيرفر (HTTP ${status}).`;

      throw err;
    }

    // Network / Unknown
    const e = new Error("❌ مشكلة في الاتصال. تأكد من الإنترنت أو حاول مرة أخرى.");
    e.meta = { code: "NETWORK_ERROR" };
    throw e;
  }
};

/* ===========================================================
 📥 تحميل PDF
=========================================================== */
export const downloadChatPDF = async (sessionId) => {
  if (!sessionId) {
    alert("⚠️ لا توجد محادثة للتحميل.");
    return;
  }

  try {
    const root = baseURL.replace(/\/api$/, "");
    const res = await fetch(`${root}/api/ai/download/${sessionId}`);

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `chat_${sessionId}.pdf`;

    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("❌ downloadChatPDF:", err);
    alert("⚠️ حدث خطأ أثناء تحميل PDF.");
  }
};
